import Dialog from 'rc-dialog'
import React, { FC, useEffect } from 'react'
import { CtaModal } from 'store/models/offers'
import { useStoreActions } from 'store'

interface Props {
  modalData: CtaModal
  onClose: () => void
}

const closeIcon = (
  <svg width="24" height="24" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg">
    <title>Close icon</title>
    <path
      d="M15.577.808l1.23 1.23-6.77 6.77 6.77 6.769-1.23 1.23-6.77-6.77-6.769 6.77-1.23-1.23 6.769-6.77-6.77-6.769L2.039.808l6.77 6.77 6.769-6.77z"
      fill="#151515"
      fillRule="evenodd"
    />
  </svg>
)

const timeIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ verticalAlign: 'sub', marginRight: '0.5em' }}
  >
    <path
      fillRule="evenodd"
      clip-rule="evenodd"
      d="M16 0C7.163 0 0 7.163 0 16C0 24.837 7.163 32 16 32C19.697 32 23.091 30.734 25.8 28.629L24.368 27.197C22.031 28.948 19.14 30 16 30C8.28 30 2 23.72 2 16C2 8.28 8.28 2 16 2C23.72 2 30 8.28 30 16C30 19.14 28.949 22.031 27.197 24.368L28.629 25.8C30.734 23.091 32 19.697 32 16C32 7.163 24.837 0 16 0ZM17 27.9999V23.9999H15V27.9999H17ZM17 3.9999V7.9999H15V3.9999H17ZM4 16.9999H8V14.9999H4V16.9999ZM28 16.9999H24V14.9999H28V16.9999Z"
      fill="#151515"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.4265 12.1795C18.5164 12.659 16.1355 15.1835 16.1355 15.1835L8.72848 7.31445L7.27148 8.68545L15.8645 17.8145L21.5725 13.8185C21.5725 13.8185 22.3366 11.6999 20.4265 12.1795Z"
      fill="#0157FF"
    />
  </svg>
)

const CTAModal: FC<Props> = ({ modalData, onClose }) => {
  const { onViewedTracking, onClickTracking } = useStoreActions(actions => actions.tracking)
  const { setCtaModal } = useStoreActions(actions => actions.offers)

  useEffect(() => {
    // fire event on mounted
    onViewedTracking({
      element: {
        type: 'element viewed',
        htmlId: '',
        location: 'Fast Pass Modal',
        name: 'Fast-Pass Modal View',
        position: 'top',
        text: 'Which form would you prefer?',
      },
    })
    // disable scroll
    if (modalData) {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden'
    }
    // unmount cleanup
    return () => {
      document.getElementsByTagName('html')[0].style.overflow = ''
    }
  }, [modalData, onViewedTracking])

  const onLinkClick = (type: 'Expedited' | 'Traditional', link?: string) => {
    window.open(link, '_blank')
    setCtaModal(null)
    onClickTracking({
      element: {
        type: 'button',
        htmlId: '',
        location: 'Fast Pass Modal',
        name: `${type} Flow`,
        position: 'top',
        text: type as string,
      },
      outcome: `${type} Flow`.toUpperCase(),
    })
  }

  return (
    <Dialog
      visible={!!modalData}
      onClose={onClose}
      wrapClassName={'center'}
      focusTriggerAfterClose={false}
      closeIcon={closeIcon}
      className="Modal-container flex-wrap bg-white overflow-hidden md:w-6/12 p-6 mt-24 rounded-xl cta-modal"
    >
      <div className="text-center">
        <div className="mb-6">
          <img
            alt={`Visit  site`}
            data-testid="cta-modal-logo"
            style={{
              maxHeight: '40px',
              maxWidth: '140px',
            }}
            src={`//www.brimg.net/system/img/inst/${modalData?.advertiserId}_hires_logo_2x.png?optimize=high&auto=webp`}
          />
        </div>
        <h3 className="type-heading-three font-circular-bold mb-8">Which experience would you prefer?</h3>
        <div className="display-flex justify-content-space-between align-items-center flex-direction-column md:flex-direction-row">
          <div className="md:px-8 w-full md:w-1/2 flex-grow border-gray sm:border-0 md:border-r ">
            <div className="mb-4">
              <button
                className="Button Button--neutral w-full md:w-10/12"
                onClick={() => onLinkClick('Traditional', modalData?.epLink)}
                data-testid="btn-traditional"
              >
                Traditional
              </button>
            </div>
            <p className="type-body-one text-slate mb-2">Get a personalized quote online.</p>
            <p className="type-body-two text-slate mb-0">{timeIcon}3 minutes</p>
          </div>
          <div className="pt-4 mt-6 md:pt-0 md:mt-0 md:px-8 w-full md:w-1/2 border-gray border-t md:border-t-0">
            <div className="mb-4">
              <button
                className="Button Button--neutral w-full md:w-10/12"
                onClick={() => onLinkClick('Expedited', modalData?.jwtLink)}
                data-testid="btn-expedited"
              >
                Expedited
              </button>
            </div>
            <p className="type-body-one text-slate mb-2">Discuss your options with a loan officer.</p>
            <p className="type-body-two text-slate mb-0">
              {timeIcon}&lt; 30 seconds<span className="Badge bg-blue p-3 ml-4 type-heading-five">NEW</span>
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default CTAModal
